import { getApolloContext } from "@apollo/client";
import { addBreadcrumb } from "@sentry/react";
import type { ReactNode } from "react";
import { useContext, createContext, useState, useEffect } from "react";

import { GlobalContext } from "@shared/context/GlobalConfig";
import type { Flipper } from "@shared/types/flipper";
import { isSSR } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import type { ExperimentTrackSharedMutation } from "../data/mutations/ExperimentTrackMutation/ExperimentTrack.mutation.generated";
import { ExperimentTrackSharedMutationDocument } from "../data/mutations/ExperimentTrackMutation/ExperimentTrack.mutation.generated";

import {
  getFeaturesToTrack,
  isFeaturePending,
  isFeatureTracked,
  markFeatureAsTracked,
  markFeatureAsPending,
  unmarkFeatureAsPending,
} from "./experimentTracking.utils";

type Flippers = Flipper[];
type FlipperResults = { name: string; result: boolean }[];

interface SelectedFlipper {
  name: string;
  enabled: boolean;
}

interface FlippersData {
  flippers: Flippers;
  untrackedExperimentFeatures: string[];
  selectedFlipper: SelectedFlipper | null;
  setFlipper: (flipper: Flipper, active: boolean) => void;
  setFlippers: (flippers: FlipperResults) => void;
  setSelectedFlipper: (value: SelectedFlipper | null) => void;
}

const FlippersContext = createContext<FlippersData>({
  flippers: [],
  untrackedExperimentFeatures: [],
  selectedFlipper: null,
  setFlipper: () => undefined,
  setFlippers: () => undefined,
  setSelectedFlipper: () => undefined,
});

interface Props {
  children: ReactNode;
  flippers: Flippers;
  untrackedExperimentFeatures?: string[];
}

const FlippersProvider = ({ flippers, untrackedExperimentFeatures = [], children }: Props) => {
  const [activeFlippers, setActiveFlippers] = useState(flippers);
  const [selectedFlipper, setSelectedFlipper] = useState<SelectedFlipper | null>(null);

  const setFlipper = (flipper: Flipper, active: boolean) => {
    if (active && !activeFlippers.includes(flipper)) {
      setActiveFlippers([...activeFlippers, flipper]);
    } else if (!active && activeFlippers.includes(flipper)) {
      const newFlippers = [...activeFlippers];
      newFlippers.splice(newFlippers.indexOf(flipper), 1);
      setActiveFlippers(newFlippers);
    }
  };

  const setFlippers = (flipperResults: FlipperResults) => {
    const newFlippers = flipperResults
      .filter(flipper => flipper.result)
      .map(({ name }) => name as Flipper);
    setActiveFlippers(newFlippers);
  };

  return (
    <FlippersContext
      value={{
        flippers: activeFlippers,
        untrackedExperimentFeatures,
        setFlipper,
        setFlippers,
        selectedFlipper,
        setSelectedFlipper,
      }}
    >
      {children}
    </FlippersContext>
  );
};

/**
 * Processes the mutation response per-feature: features present in the response
 * are marked as tracked, while missing features are un-marked as pending so they
 * can be retried on the next render/visit.
 */
const processTrackingResponse = (
  data: ExperimentTrackSharedMutation | null | undefined,
  featuresToTrack: string[]
): void => {
  const trackedNames = new Set(data?.experimentTrack?.results?.map(r => r.featureName) ?? []);

  const missingFeatures: string[] = [];

  featuresToTrack.forEach(feature => {
    if (trackedNames.has(feature)) {
      markFeatureAsTracked(feature);
    } else {
      unmarkFeatureAsPending(feature);
      missingFeatures.push(feature);
    }
  });

  if (missingFeatures.length > 0) {
    addBreadcrumb({
      category: "experimentTracking",
      message: "Experiment tracking response missing expected results",
      level: "warning",
      data: { missingFeatures, featuresToTrack, response: data },
    });
  }
};

const useExperimentTracking = (featuresToTrack: string[]) => {
  const apolloContext = useContext(getApolloContext());
  const apolloClient = apolloContext?.client ?? null;
  const { e2e } = useContext(GlobalContext);

  const featuresKey = featuresToTrack.join(",");

  useEffect(() => {
    if (isSSR()) return;
    if (e2e) return;
    if (!apolloClient) return;
    if (featuresToTrack.length === 0) return;

    // Re-filter at effect execution time to prevent duplicate mutations when
    // multiple components check the same experiment flipper in one render cycle,
    // or when React StrictMode double-fires effects.
    const stillNeeded = featuresToTrack.filter(f => !isFeatureTracked(f) && !isFeaturePending(f));
    if (stillNeeded.length === 0) return;

    stillNeeded.forEach(markFeatureAsPending);

    apolloClient
      .mutate({
        mutation: ExperimentTrackSharedMutationDocument,
        variables: { input: { featureNames: stillNeeded } },
      })
      .then(result => {
        processTrackingResponse(result.data, stillNeeded);
      })
      .catch(error => {
        stillNeeded.forEach(unmarkFeatureAsPending);

        addBreadcrumb({
          category: "experimentTracking",
          message: "Failed to track experiment",
          level: "warning",
          data: { featuresToTrack: stillNeeded, error: error.message },
        });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- featuresToTrack is a new array each render; using stringified featuresKey for stable comparison
  }, [featuresKey, apolloClient, e2e]);
};

/**
 * Hook to check if flippers are enabled and trigger experiment tracking.
 *
 * Experiment tracking is best-effort and fire-once-per-session:
 * - Each experiment feature is tracked at most once per page session
 * - Tracking failures are logged but don't retry (user will be tracked on next visit)
 * - Multiple components checking the same flipper won't cause duplicate tracking
 */
const useFlippers = (...args: Flipper[]): boolean[] => {
  const { flippers, untrackedExperimentFeatures } = useContext(FlippersContext);

  const results = args.map(flipper => flippers.includes(flipper));
  const featuresToTrack = getFeaturesToTrack(args, untrackedExperimentFeatures);

  useExperimentTracking(featuresToTrack);

  return results;
};

export { FlippersContext, FlippersProvider, useFlippers };
